import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Real, irreversible deletion. Confirmation is enforced server-side too:
// the exact username is always required, plus the password for email accounts.
export default function DeleteAccountDialog({ open, onOpenChange }) {
  const { user, setUser } = useAuth();
  const [confirmUsername, setConfirmUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const isGoogle = user?.auth_provider === "google";
  const usernameMatches = confirmUsername.trim().replace(/^@/, "").toLowerCase() === (user?.username || "").toLowerCase();
  const ready = usernameMatches && (isGoogle || password.length > 0);

  const submit = async () => {
    setBusy(true);
    try {
      await api.delete("/auth/me", {
        data: { confirm_username: confirmUsername.trim(), password: isGoogle ? null : password },
      });
      toast.success("Your account has been permanently deleted.");
      setUser(null);
      window.location.href = "/";
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl sm:max-w-md" data-testid="delete-account-dialog">
        <DialogHeader>
          <div className="mb-1 flex h-10 w-10 items-center justify-center rounded-2xl bg-red-50">
            <AlertTriangle className="h-5 w-5 text-red-500" />
          </div>
          <DialogTitle className="font-heading text-lg font-extrabold">Delete your account?</DialogTitle>
          <DialogDescription className="text-sm">
            This cannot be undone. Your profile, photos, friends, friend requests, private conversations and public
            chat messages are permanently removed, and <span className="font-semibold">@{user?.username}</span> is
            released for anyone else to claim.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Type @{user?.username} to confirm
            </Label>
            <Input
              value={confirmUsername}
              onChange={(e) => setConfirmUsername(e.target.value)}
              placeholder={`@${user?.username}`}
              data-testid="input-confirm-username"
              className="rounded-xl border-slate-200 bg-slate-50 focus-visible:bg-white"
            />
          </div>

          {isGoogle ? (
            <p className="rounded-xl bg-slate-50 p-3 text-xs text-slate-500" data-testid="delete-google-note">
              You signed in with Google, so no password is needed — typing your username is the confirmation.
            </p>
          ) : (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Your password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Re-enter your password"
                data-testid="input-confirm-password"
                className="rounded-xl border-slate-200 bg-slate-50 focus-visible:bg-white"
              />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" className="rounded-full" onClick={() => onOpenChange(false)} data-testid="delete-account-cancel">
            Keep my account
          </Button>
          <Button
            onClick={submit}
            disabled={!ready || busy}
            data-testid="delete-account-confirm"
            className="rounded-full bg-red-500 text-white hover:bg-red-600"
          >
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Delete permanently
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
